/**
 * Duplicate Detection Engine — keeps the factory from cloning itself.
 *
 * Similarity is a weighted gene-by-gene comparison of two DNA records,
 * 0 (nothing shared) to 1 (identical). Seed colors compare by hue distance
 * instead of string equality — #ef4444 and #f05050 are the same design
 * decision even though the hex differs.
 *
 * A candidate whose similarity to ANY existing template exceeds the
 * threshold (default 0.7 per product spec) is a duplicate and is rejected.
 */

import { hexHue } from './designDNAEngine';

/** Gene weights — sum to 1. The genes a viewer notices most weigh most. */
export const GENE_WEIGHTS = {
  layout: 0.16,
  paletteTheme: 0.12,
  harmony: 0.07,
  seed: 0.09,
  dark: 0.04,
  typographyTheme: 0.12,
  typeScale: 0.04,
  spacing: 0.06,
  decorationPack: 0.10,
  shapeStyle: 0.04,
  offerStyle: 0.06,
  footerStyle: 0.04,
  imageCount: 0.03,
  imageStyle: 0.03,
};
// heroPosition/ctaPosition are derived from layout, so scoring them would
// double-count the layout gene.

const seedSimilarity = (a, b) => {
  const diff = Math.abs(hexHue(a) - hexHue(b));
  const dist = Math.min(diff, 360 - diff); // hue wraps
  return dist <= 15 ? 1 : dist <= 40 ? 0.5 : 0;
};

/** Weighted DNA similarity, 0–1. */
export const dnaSimilarity = (a, b) => {
  let score = 0;
  Object.entries(GENE_WEIGHTS).forEach(([gene, weight]) => {
    if (gene === 'seed') score += weight * seedSimilarity(a.seed, b.seed);
    else if (a[gene] === b[gene]) score += weight;
  });
  return Number(score.toFixed(4));
};

export const DEFAULT_SIMILARITY_THRESHOLD = 0.7;

/**
 * Compare a candidate against a set of existing DNA records.
 * Returns { duplicate, maxSimilarity, nearest } — nearest is the existing
 * DNA it most resembles (null when the set is empty).
 */
export const checkDuplicate = (candidate, existing, threshold = DEFAULT_SIMILARITY_THRESHOLD) => {
  let maxSimilarity = 0;
  let nearest = null;
  for (const dna of existing) {
    const s = dnaSimilarity(candidate, dna);
    if (s > maxSimilarity) {
      maxSimilarity = s;
      nearest = dna;
      if (maxSimilarity >= 1) break; // exact clone — no point scanning on
    }
  }
  return { duplicate: maxSimilarity > threshold, maxSimilarity, nearest };
};
